import { useState } from "react";
import { Alert, Button, TextArea, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { StoreHighlightView, StoreSocialLink } from "@/lib/api/types";
import { useUpdateStoreMutation } from "@/modules/settings/services/useUpdateStoreMutation";
import { BrandingLogoSheet } from "@/modules/settings/components/BrandingLogoSheet";
import { BrandingHighlightsSheet } from "@/modules/settings/components/BrandingHighlightsSheet";
import { BrandingWhatsappSheet } from "@/modules/settings/components/BrandingWhatsappSheet";
import { BrandingSocialLinksSheet } from "@/modules/settings/components/BrandingSocialLinksSheet";
import { Card, Icons, Label, tokens } from "./primitives";

type BrandingPanelProps = {
  storeSlug: string;
  storeName: string;
  description: string | null;
  descriptionEn: string | null;
  logoUrl: string | null;
  whatsappNumber: string | null;
  urls: StoreSocialLink[];
  highlights: StoreHighlightView[];
  /** Full absolute buyer storefront URL, e.g. https://my-store.trioty.com/ar */
  storefrontUrl: string;
  isMobile?: boolean;
};

const DESCRIPTION_MAX = 280;

export function BrandingPanel({
  storeSlug,
  storeName,
  description,
  descriptionEn,
  logoUrl,
  whatsappNumber,
  urls,
  highlights,
  storefrontUrl,
  isMobile = false,
}: BrandingPanelProps) {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;

  const [logoOpen, setLogoOpen] = useState(false);
  const [highlightsOpen, setHighlightsOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const initials = (storeName || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const copyHandle = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(storefrontUrl);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Identity card — logo, read-only name, editable description */}
      <Card title={t(ss.identityTitle)} desc={t(ss.identityDesc)}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "minmax(0, 1fr)"
              : "120px minmax(0, 1fr)",
            gap: isMobile ? 16 : 20,
            alignItems: "start",
          }}
        >
          <div>
            <Label>{t(ss.storeLogo)}</Label>
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: 14,
                marginTop: 6,
                background: logoUrl
                  ? `center / cover no-repeat url(${logoUrl})`
                  : `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 32,
                letterSpacing: -1,
                boxShadow: "0 4px 12px rgba(15,20,40,0.08)",
              }}
            >
              {logoUrl ? null : initials}
            </div>
            <div style={{ marginTop: 8 }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLogoOpen(true)}
              >
                {logoUrl
                  ? t(keys.seller.storeLogoReplace)
                  : t(keys.common.upload)}
              </Button>
            </div>
            <div style={{ fontSize: 11, color: tokens.text3, marginTop: 6 }}>
              {t(ss.logoHint)}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <Label>{t(ss.storeName)}</Label>
              <div
                style={{
                  marginTop: 6,
                  padding: "10px 12px",
                  borderRadius: 9,
                  background: tokens.bgSoft,
                  border: `1px solid ${tokens.border}`,
                  fontSize: 14,
                  fontWeight: 600,
                  color: tokens.text,
                }}
              >
                {storeName}
              </div>
              <div style={{ fontSize: 11, color: tokens.text3, marginTop: 6 }}>
                {t(keys.seller.storeNameReadonlyHelper)}
              </div>
            </div>

            <DescriptionEditor
              storeSlug={storeSlug}
              description={description}
              descriptionEn={descriptionEn}
            />
          </div>
        </div>
      </Card>

      {/* Highlights card — read-only summary, edit via sheet */}
      <Card
        title={t(ss.highlightsTitle)}
        desc={t(ss.highlightsDesc)}
        action={
          <Button
            variant="link"
            size="sm"
            onClick={() => setHighlightsOpen(true)}
          >
            {t(ss.manage)}
          </Button>
        }
      >
        {highlights.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {highlights.map((h) => (
              <div
                key={h.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${tokens.border}`,
                  background: tokens.bgSoft,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: tokens.text,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {h.textAr}
                  </div>
                  {h.textEn ? (
                    <div
                      dir="ltr"
                      style={{
                        fontSize: 12,
                        color: tokens.text3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {h.textEn}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: 24,
              borderRadius: 10,
              border: `1px dashed ${tokens.borderStrong}`,
              textAlign: "center",
              color: tokens.text3,
              fontSize: 13,
            }}
          >
            {t(keys.seller.storeHighlightsEmpty)}
          </div>
        )}
      </Card>

      {/* Contact card — WhatsApp number, edit via sheet */}
      <Card
        title={t(ss.contactTitle)}
        desc={t(ss.contactDesc)}
        action={
          <Button
            variant="link"
            size="sm"
            onClick={() => setWhatsappOpen(true)}
          >
            {whatsappNumber ? t(ss.change) : t(ss.connect)}
          </Button>
        }
      >
        {whatsappNumber ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 9,
              background: tokens.successSoft,
              border: `1px solid color-mix(in oklab, ${tokens.success} 28%, white)`,
              fontSize: 12,
              color: tokens.successText,
              flexWrap: "wrap",
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0 }}>
              {"✓"}
            </span>
            <b>{t(ss.whatsappLinked)}</b>
            <span
              dir="ltr"
              className="font-mono"
              style={{ color: tokens.successText }}
            >
              {whatsappNumber}
            </span>
          </div>
        ) : (
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 9,
              background: tokens.bgSoft,
              border: `1px solid ${tokens.border}`,
              fontSize: 12,
              color: tokens.text3,
            }}
          >
            {t(ss.whatsappHint)}
          </div>
        )}
      </Card>

      {/* Social links card — read-only summary, edit via sheet */}
      <Card
        title={t(ss.socialTitle)}
        desc={t(ss.socialDesc)}
        action={
          <Button variant="link" size="sm" onClick={() => setSocialOpen(true)}>
            {urls.length > 0 ? t(ss.change) : t(ss.connect)}
          </Button>
        }
      >
        {urls.length > 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 9,
              background: tokens.successSoft,
              border: `1px solid color-mix(in oklab, ${tokens.success} 28%, white)`,
              fontSize: 12,
              color: tokens.successText,
              flexWrap: "wrap",
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0 }}>
              {"✓"}
            </span>
            <b>{t(ss.socialLinkedCount, { count: urls.length })}</b>
          </div>
        ) : (
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 9,
              background: tokens.bgSoft,
              border: `1px solid ${tokens.border}`,
              fontSize: 12,
              color: tokens.text3,
            }}
          >
            {t(ss.socialHint)}
          </div>
        )}
      </Card>

      {/* Storefront URL card — read-only handle + copy */}
      <Card title={t(ss.storefrontUrlTitle)} desc={t(ss.storefrontUrlDesc)}>
        <div
          dir="ltr"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 12px",
            borderRadius: 9,
            background: tokens.bgSoft,
            border: `1px solid ${tokens.border}`,
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            minWidth: 0,
          }}
        >
          <span
            style={{
              color: tokens.text,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {storefrontUrl.replace(/^https?:\/\//, "")}
          </span>
          <Button
            variant="link"
            size="sm"
            onClick={copyHandle}
            aria-label={t(ss.copyUrl)}
            className="ms-auto"
            style={{
              color: copied ? tokens.successText : tokens.text2,
              flexShrink: 0,
            }}
            leadingIcon={
              copied ? <span aria-hidden="true">{"✓"}</span> : Icons.copy
            }
          />
        </div>
      </Card>

      <BrandingLogoSheet
        isOpen={logoOpen}
        onClose={() => setLogoOpen(false)}
        logoUrl={logoUrl}
      />
      <BrandingHighlightsSheet
        isOpen={highlightsOpen}
        onClose={() => setHighlightsOpen(false)}
        storeSlug={storeSlug}
        initialHighlights={highlights}
      />
      <BrandingWhatsappSheet
        isOpen={whatsappOpen}
        onClose={() => setWhatsappOpen(false)}
        storeSlug={storeSlug}
        whatsappNumber={whatsappNumber}
      />
      <BrandingSocialLinksSheet
        isOpen={socialOpen}
        onClose={() => setSocialOpen(false)}
        storeSlug={storeSlug}
        urls={urls}
      />
    </div>
  );
}

type DescriptionEditorProps = {
  storeSlug: string;
  description: string | null;
  descriptionEn: string | null;
};

function DescriptionEditor({
  storeSlug,
  description,
  descriptionEn,
}: DescriptionEditorProps): React.ReactElement {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: updateStore, isPending } = useUpdateStoreMutation();
  const [draft, setDraft] = useState(description ?? "");
  const [draftEn, setDraftEn] = useState(descriptionEn ?? "");
  const [error, setError] = useState<string | null>(null);
  // Re-seed the drafts when the server values change (React-recommended
  // "adjust state during render" pattern — avoids a sync effect).
  const [lastDescription, setLastDescription] = useState(description);
  const [lastDescriptionEn, setLastDescriptionEn] = useState(descriptionEn);
  if (description !== lastDescription || descriptionEn !== lastDescriptionEn) {
    setLastDescription(description);
    setLastDescriptionEn(descriptionEn);
    setDraft(description ?? "");
    setDraftEn(descriptionEn ?? "");
    setError(null);
  }

  const normalized = draft.trim();
  const normalizedEn = draftEn.trim();
  const isDirty =
    normalized !== (description ?? "").trim() ||
    normalizedEn !== (descriptionEn ?? "").trim();

  const handleSave = async (): Promise<void> => {
    setError(null);
    try {
      await updateStore({
        storeSlug,
        description: normalized,
        descriptionEn: normalizedEn,
      });
      toast.success(t(keys.seller.storeDescriptionSavedNotice));
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : t(keys.errors.generic),
      );
    }
  };

  const field = (
    lang: "ar" | "en",
    value: string,
    setValue: (v: string) => void,
  ): React.ReactElement => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: tokens.text3 }}>
        {lang === "ar" ? "العربية" : "English"}
      </span>
      <TextArea
        name={lang === "ar" ? "storeDescription" : "storeDescriptionEn"}
        value={value}
        rows={3}
        dir={lang === "ar" ? "rtl" : "ltr"}
        maxLength={DESCRIPTION_MAX}
        placeholder={t(keys.seller.storeDescriptionPlaceholder)}
        hasError={Boolean(error)}
        onChange={(e) => {
          setError(null);
          setValue(e.target.value);
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <span
          className="font-mono"
          style={{ fontSize: 11, color: tokens.text3 }}
        >
          {value.trim().length}/{DESCRIPTION_MAX}
        </span>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Label>{t(keys.seller.storeDescriptionLabel)}</Label>
      {field("ar", draft, setDraft)}
      {field("en", draftEn, setDraftEn)}
      <span style={{ fontSize: 11, color: tokens.text3 }}>
        {t(keys.seller.storeDescriptionHelper)}
      </span>
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      {isDirty ? (
        <div>
          <Button
            variant="primary"
            size="sm"
            disabled={isPending}
            onClick={() => void handleSave()}
          >
            {isPending ? t(keys.common.loading) : t(keys.common.save)}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
